/**
 * Input validation utilities
 * Centralizes validation logic for forms and user inputs
 */

/**
 * Validates match submission form data
 * @param {FormData} formData - Form data from match submission
 * @returns {Object} Validation result with isValid boolean and error message
 */
export const validateMatchSubmission = formData => {
  const t1p1 = formData.get('t1p1');
  const t1p2 = formData.get('t1p2');
  const t2p1 = formData.get('t2p1');
  const t2p2 = formData.get('t2p2');
  const score1 = parseInt(formData.get('score1'));
  const score2 = parseInt(formData.get('score2'));

  // Check for 4 unique players
  const allPlayers = [t1p1, t1p2, t2p1, t2p2].filter(Boolean);
  if (new Set(allPlayers).size !== 4) {
    return {
      isValid: false,
      error: 'You must select 4 unique players.',
    };
  }

  // Validate scores are numbers
  if (isNaN(score1) || isNaN(score2)) {
    return {
      isValid: false,
      error: 'Please enter valid scores.',
    };
  }

  // Validate game rules (one team must win with exactly 10)
  const isTeam1Win = score1 === 10 && score2 < 10;
  const isTeam2Win = score2 === 10 && score1 < 10;

  if (!isTeam1Win && !isTeam2Win) {
    return {
      isValid: false,
      error:
        'Invalid Score. One team must win with exactly 10 goals. ' +
        '(e.g., 10-8, 10-0)',
    };
  }

  return {
    isValid: true,
    error: null,
    data: {
      players: { t1p1, t1p2, t2p1, t2p2 },
      scores: { score1, score2 },
    },
  };
};

/**
 * Validates player name input
 * @param {string} name - Player name to validate
 * @param {Array} existingPlayers - Array of existing players to check for
 * duplicates
 * @returns {Object} Validation result with isValid boolean and error message
 */
export const validatePlayerName = (name, existingPlayers = []) => {
  if (!name || name.trim().length === 0) {
    return {
      isValid: false,
      error: 'Player name is required.',
    };
  }

  const trimmedName = name.trim();

  if (trimmedName.length < 2) {
    return {
      isValid: false,
      error: 'Player name must be at least 2 characters long.',
    };
  }

  if (trimmedName.length > 50) {
    return {
      isValid: false,
      error: 'Player name must be less than 50 characters.',
    };
  }

  // Check for duplicate names (case-insensitive)
  const normalizedName = trimmedName.toLowerCase();
  const isDuplicate = existingPlayers.some(
    player => player && player.name && player.name.toLowerCase() === normalizedName
  );

  if (isDuplicate) {
    return {
      isValid: false,
      error: `Player name "${trimmedName}" already exists. Please use a unique name.`,
    };
  }

  return {
    isValid: true,
    error: null,
    data: {
      name: trimmedName,
      initials: trimmedName
        .split(' ')
        .map(n => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase(),
    },
  };
};

/**
 * Validates score input for match forms
 * @param {string} scoreInput - Score input value
 * @returns {Object} Validation result with isValid boolean and error message
 */
export const validateScore = scoreInput => {
  const score = parseInt(scoreInput);

  if (isNaN(score)) {
    return {
      isValid: false,
      error: 'Score must be a valid number.',
    };
  }

  if (score < 0) {
    return {
      isValid: false,
      error: 'Score cannot be negative.',
    };
  }

  if (score > 10) {
    return {
      isValid: false,
      error: 'Score cannot be greater than 10.',
    };
  }

  return {
    isValid: true,
    error: null,
    data: { score },
  };
};
